import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

// Fetch all accommodations
export const fetchAccommodations = createAsyncThunk(
  "accommodations/fetchAccommodations",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get("/accommodationData/");
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert a new accommodation
export const insertAccommodation = createAsyncThunk(
  "accommodations/insertAccommodation",
  async (accommodation, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(accommodation).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      formData.append("status", "A");

      const { data } = await apiClient.post("/accommodationData/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (error) {
      console.error("Insert Accommodation error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update an accommodation
export const updateAccommodation = createAsyncThunk(
  "accommodations/updateAccommodation",
  async (accommodation, { rejectWithValue }) => {
    try {
      if (!accommodation.id) throw new Error("Missing accommodation ID");

      const formData = new FormData();
      Object.entries(accommodation).forEach(([key, value]) => {
        if (key !== "id" && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      formData.append("status", "A");

      const { data } = await apiClient.put(
        `/accommodationData/${accommodation.id}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    } catch (error) {
      console.error("Update Accommodation error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete an accommodation
export const deleteAccommodation = createAsyncThunk(
  "accommodations/deleteAccommodation",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/accommodationData/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const accommodationsSlice = createSlice({
  name: "accommodations",
  initialState: {
    accommodations: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccommodations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccommodations.fulfilled, (state, action) => {
        state.loading = false;
        state.accommodations = action.payload;
      })
      .addCase(fetchAccommodations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertAccommodation.fulfilled, (state, action) => {
        state.accommodations.push(action.payload);
      })
      .addCase(insertAccommodation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAccommodation.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.accommodations.findIndex((acc) => acc.id === updated.id);
        if (index !== -1) {
          state.accommodations[index] = updated;
        }
      })
      .addCase(updateAccommodation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteAccommodation.fulfilled, (state, action) => {
        state.accommodations = state.accommodations.filter((acc) => acc.id !== action.payload);
      })
      .addCase(deleteAccommodation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default accommodationsSlice.reducer;
